import React, { useCallback, useMemo, useState } from 'react';
import { FaExclamationCircle } from 'react-icons/fa';

import EsxiHeader from './components/EsxiHeader';
import HostSummary from './components/HostSummary';
import CatalogWarnings from './components/CatalogWarnings';
import FilterBar from './components/FilterBar';
import VmGroups from './components/VmGroups';
import VmActionModal from './components/VmActionModal';
import EsxiHelpModal from './components/EsxiHelpModal';
import { LoadingState, ErrorState, EmptyInventory } from './components/StateBanners';

import { useEsxiInventory } from './hooks/useEsxiInventory';
import { useVmAction } from './hooks/useVmAction';
import { useViewMode } from './hooks/useViewMode';

import {
  EMPTY_FILTERS,
  buildFilterOptions,
  countByPowerState,
  filterVms,
  groupVms,
  hasActiveFilters,
} from './utils/format';

/**
 * Pagina "Ambienti di test": dashboard ESXi 1 di SiSketron.
 *
 * Accessibile a tutti gli utenti SiSketron autenticati (nessun controllo per
 * nome utente): il montaggio avviene sotto ProtectedLayout, che garantisce già
 * l'autenticazione. Tutta la comunicazione passa dal backend `/api/esxi`; il
 * browser non si collega mai direttamente a ESXi.
 */
export default function EsxiDashboardPage() {
  const {
    summary,
    vms,
    catalogWarnings,
    loading,
    refreshing,
    error,
    lastUpdated,
    stale,
    refresh,
    applyOperationResult,
  } = useEsxiInventory();

  // Lo stato finale arriva dall'operazione dopo la verifica backend: viene
  // applicato subito alla UI, mentre il refresh live aggiorna poi metriche,
  // VMware Tools e IP senza lasciare visibili pulsanti obsoleti.
  const handleSettled = useCallback((catalogId, operation) => {
    applyOperationResult(catalogId, operation);
    refresh();
  }, [applyOperationResult, refresh]);
  const { operations, submit, dismiss, isBusy } = useVmAction(handleSettled);

  const [viewMode, setViewMode] = useViewMode();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [pending, setPending] = useState(null); // { vm, action } | null
  const [helpOpen, setHelpOpen] = useState(false);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);
  const resetFilters = useCallback(() => setFilters(EMPTY_FILTERS), []);

  const filterOptions = useMemo(() => buildFilterOptions(vms), [vms]);
  const filteredVms = useMemo(() => filterVms(vms, filters), [vms, filters]);
  const groups = useMemo(() => groupVms(filteredVms), [filteredVms]);
  const filtersActive = hasActiveFilters(filters);
  const displayedSummary = useMemo(() => {
    if (!summary) return null;
    const counts = countByPowerState(vms);
    return {
      ...summary,
      counts: {
        total: counts.total,
        on: counts.ON,
        off: counts.OFF,
        suspended: counts.SUSPENDED,
        unknown: counts.UNKNOWN,
      },
    };
  }, [summary, vms]);

  const handleRequestAction = useCallback((vm, action) => {
    setPending({ vm, action });
  }, []);

  const handleConfirm = useCallback(
    (vm, action, reason) => {
      submit(vm.catalogId, action, reason, vm.name);
      setPending(null);
    },
    [submit],
  );

  // Errore bloccante solo quando non abbiamo alcun dato da mostrare.
  const blockingError = error && vms.length === 0 && !summary;

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-10">
      <EsxiHeader
        summary={displayedSummary}
        error={error}
        stale={stale}
        lastUpdated={lastUpdated}
        refreshing={refreshing}
        onRefresh={refresh}
        onHelp={() => setHelpOpen(true)}
      />

      {loading ? (
        <LoadingState />
      ) : blockingError ? (
        <ErrorState error={error} onRetry={refresh} retrying={refreshing} />
      ) : (
        <>
          {/* Errore non bloccante: mostriamo comunque i dati (eventualmente stale). */}
          {error && (
            <div
              className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300"
              role="alert"
            >
              <FaExclamationCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                {error.message} I dati mostrati potrebbero non essere aggiornati.
              </span>
            </div>
          )}

          <HostSummary summary={displayedSummary} />

          <CatalogWarnings warnings={catalogWarnings} />

          <FilterBar
            filters={filters}
            options={filterOptions}
            viewMode={viewMode}
            onSetFilter={setFilter}
            onReset={resetFilters}
            onSetViewMode={setViewMode}
            resultCount={filteredVms.length}
          />

          {filteredVms.length === 0 ? (
            <EmptyInventory filtered={filtersActive} onResetFilters={resetFilters} />
          ) : (
            <VmGroups
              groups={groups}
              viewMode={viewMode}
              catalogWarnings={catalogWarnings}
              operations={operations}
              isBusy={isBusy}
              onRequestAction={handleRequestAction}
              onDismissOp={dismiss}
            />
          )}
        </>
      )}

      {pending && (
        <VmActionModal
          vm={pending.vm}
          action={pending.action}
          onConfirm={handleConfirm}
          onClose={() => setPending(null)}
        />
      )}

      {helpOpen && <EsxiHelpModal onClose={() => setHelpOpen(false)} />}
    </div>
  );
}
